import React, { useContext, useEffect } from "react";
import RegisterLayout from "./RegisterLayout";

import {
    Controller,
    FormProvider,
    SubmitHandler,
    useForm,
} from "react-hook-form";

import InputFieldV2 from "../../../lib/input/InputFieldV2";
import DropDownV2 from "../../../global/ui/DropDownV2";

import { basicDetailFormData } from "../../../data/formData";
import { apiConstants } from "../../../utils/apiConstants";

import { ErrorMessage } from "@hookform/error-message";

import { errorMessage } from "../../../utils/constants";

import { preConfigDeptData } from "../../../data/data";

import { normalize, showToast } from "../../../utils";
import UserSnap from "../../../global/ui/UserSnap";
import { MyContext } from "../../../context";

interface StepAboutYourselfProps {
    onNext: (data: any) => void;
}

export const daysData: Array<any> = [
    { label: "M", value: "1" },
    { label: "TU", value: "2" },
    { label: "W", value: "3" },
    { label: "TH", value: "4" },
    { label: "F", value: "5" },
    { label: "SA", value: "6" },
    { label: "SU", value: "7" },
];

const StepAboutYourself: React.FC<
    StepAboutYourselfProps
> = ({ onNext }) => {

    const { userData } = useContext(MyContext);

    const basicInfoFormData = useForm({
        mode: "onChange",

        defaultValues: {
            ...basicDetailFormData(userData),
        },
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        register,
        formState: { errors },
    } = basicInfoFormData;

    const departments =
        watch("departments") || [];

    const workingDays =
        watch(apiConstants.working_days) || [];

    const handleAddAnother = () => {

        if (departments.length >= 3) return;

        const lastRow =
            departments[
            departments.length - 1
            ];

        if (
            !lastRow?.department ||
            !lastRow?.roles?.length
        ) {
            showToast(
                "Please select department and role",
                "error"
            );

            return;
        }

        setValue(
            "departments",
            [
                ...departments,
                {
                    department: null,
                    roles: [],
                },
            ],
            {
                shouldValidate: true,
            }
        );
    };

    const handleRemoveRow = (
        index: number
    ) => {

        const updated =
            departments.filter(
                (_: any, i: number) =>
                    i !== index
            );

        setValue(
            "departments",
            updated,
            {
                shouldValidate: true,
            }
        );
    };

    const toggleDay = (key: string) => {

        const updatedDays =
            workingDays.includes(key)
                ? workingDays.filter(
                    (d: string) =>
                        d !== key
                )
                : [
                    ...workingDays,
                    key,
                ];

        setValue(
            apiConstants.working_days,
            updatedDays,
            {
                shouldValidate: true,
            }
        );
    };

    const onSubmit:
        SubmitHandler<any> = (
            values: any
        ) => {

            if (workingDays?.length <= 0) {
                showToast('Please select working days.', 'error');
                return
            }

            const depRoles =
                values.departments.map(
                    (item: any) => ({
                        dep:
                            item.department
                                ?.label,

                        role:
                            item.roles.map(
                                (
                                    roleItem: any
                                ) =>
                                    roleItem.label
                            ),
                    })
                );

            const fd = new FormData();
            fd.append(apiConstants.IMDB_link, values[apiConstants.IMDB_link])
            fd.append(apiConstants.biography, values[apiConstants.biography])
            fd.append(apiConstants.role, JSON.stringify(depRoles))
            fd.append(apiConstants.working_days, workingDays)

            if (values[apiConstants.profile_image] !== userData?.profile_image) {
                fd.append(apiConstants.profile_image, values[apiConstants.profile_image] as never)
            }

            onNext(fd);
        };

    useEffect(() => {

        register(
            apiConstants.working_days,
            {
                required:
                    errorMessage.required_validation(
                        "Working days"
                    ),
            }
        );

        register(apiConstants.profile_image, {
            required:
                "Profile image is required",
        });

    }, [register]);

    useEffect(() => {
        if (userData?.id) {
            const mappedDepartments =
                userData?.crew_member_profile?.role?.map(
                    (item: any) => {

                        const selectedDepartment =
                            preConfigDeptData.find(
                                (dept: any) =>
                                    dept.label === item.dep
                            );

                        const selectedRoles =
                            selectedDepartment?.roles?.filter(
                                (roleItem: any) =>
                                    item.role?.includes(
                                        roleItem.label
                                    )
                            ) || [];

                        return {
                            department:
                                selectedDepartment || null,

                            roles: selectedRoles,
                        };
                    }
                ) || [
                    {
                        department: null,
                        roles: [],
                    },
                ];
            basicInfoFormData.reset({
                [apiConstants.IMDB_link]: userData?.crew_member_profile?.imdb_link || "",
                [apiConstants.biography]: userData?.crew_member_profile?.bio || '',
                [apiConstants.working_days]: userData?.crew_member_profile?.working_days ? normalize(userData?.crew_member_profile?.working_days) : [],
                [apiConstants.profile_image]: userData?.profile_image || null,
                departments: mappedDepartments,
            })
        }
    }, [userData?.id])

    return (
        <RegisterLayout showLogout hideHeader>

            <FormProvider
                {...basicInfoFormData}
            >

                <form
                    onSubmit={handleSubmit(
                        onSubmit
                    )}
                    noValidate
                >

                    <div className="register-step">

                        {/* Heading */}

                        <div className="register-step__heading-block">

                            <h1 className="register-step__title">
                                A bit about
                                <br />
                                yourself
                            </h1>

                            <p className="register-step__description">
                                The more you tell
                                us about yourself
                                the more
                                comprehensive your
                                profile will look.
                            </p>

                        </div>

                        <hr className="register-step__divider" />

                        {/* Avatar */}

                        <div style={{ position: 'relative' }} className="about__avatar-row">

                            <UserSnap
                                onChange={(value) => basicInfoFormData.setValue(apiConstants.profile_image, value, { shouldValidate: true })}
                                value={basicInfoFormData.getValues(apiConstants.profile_image)}
                                label='Profile picture'
                            />

                            <button
                                type="button"
                                className="about__avatar-label"
                            >
                                Change your
                                profile picture
                            </button>

                            <ErrorMessage
                                errors={errors}
                                name={apiConstants.profile_image}
                                render={({
                                    message,
                                }) => (
                                    <span style={{ position: 'absolute', bottom: '-25px' }} className="input-field-error-text">
                                        {
                                            message
                                        }
                                    </span>
                                )}
                            />
                        </div>

                        {/* Department + Roles */}

                        <div className="about__dept-list">

                            {departments.map(
                                (
                                    item: any,
                                    index: number
                                ) => {

                                    const selectedDepartment =
                                        preConfigDeptData.find(
                                            (
                                                dept: any
                                            ) =>
                                                dept.value ===
                                                item
                                                    ?.department
                                                    ?.value
                                        );

                                    return (
                                        <div
                                            key={
                                                index
                                            }
                                            className="about__dept-row"
                                        >

                                            {/* Department */}

                                            <Controller
                                                control={
                                                    control
                                                }
                                                name={`departments.${index}.department`}
                                                rules={{
                                                    required:
                                                        "Department is required",
                                                }}
                                                render={({
                                                    field,
                                                    fieldState,
                                                }) => (
                                                    <DropDownV2
                                                        name={`department-${index}`}
                                                        label="Select Department"
                                                        placeholder="Select Department"
                                                        data={
                                                            preConfigDeptData
                                                        }
                                                        selectedItems={
                                                            field.value
                                                        }
                                                        onSelect={(
                                                            option: any
                                                        ) => {

                                                            const alreadySelected =
                                                                departments.some(
                                                                    (
                                                                        dep: any,
                                                                        i: number
                                                                    ) =>
                                                                        dep
                                                                            ?.department
                                                                            ?.value ===
                                                                        option.value &&
                                                                        i !==
                                                                        index
                                                                );

                                                            if (
                                                                alreadySelected
                                                            ) {
                                                                showToast(
                                                                    "Department already selected",
                                                                    "error"
                                                                );

                                                                return;
                                                            }

                                                            field.onChange(
                                                                option
                                                            );

                                                            setValue(
                                                                `departments.${index}.roles`,
                                                                []
                                                            );
                                                        }}
                                                        isSearchable
                                                        errorMessage={
                                                            fieldState
                                                                .error
                                                                ?.message
                                                        }
                                                    />
                                                )}
                                            />

                                            {/* Roles */}

                                            <Controller
                                                control={
                                                    control
                                                }
                                                name={`departments.${index}.roles`}
                                                rules={{
                                                    validate:
                                                        (
                                                            value
                                                        ) =>
                                                            value?.length >
                                                            0 ||
                                                            "Role is required",
                                                }}
                                                render={({
                                                    field,
                                                    fieldState,
                                                }) => (
                                                    <DropDownV2
                                                        name={`roles-${index}`}
                                                        label="Select Roles"
                                                        placeholder="Select Roles"
                                                        data={
                                                            selectedDepartment?.roles ||
                                                            []
                                                        }
                                                        selectedItems={
                                                            field.value
                                                        }
                                                        onSelect={
                                                            field.onChange
                                                        }
                                                        isSearchable
                                                        isMultiSelect
                                                        errorMessage={
                                                            fieldState
                                                                .error
                                                                ?.message
                                                        }
                                                    />
                                                )}
                                            />

                                        </div>
                                    );
                                }
                            )}

                        </div>

                        {/* Add Another */}

                        {departments.length <
                            3 && (
                                <div className="about__add-row">

                                    <button
                                        type="button"
                                        className="btn-add-another"
                                        onClick={
                                            handleAddAnother
                                        }
                                    >
                                        Add Another
                                    </button>

                                </div>
                            )}

                        {/* IMDB */}

                        <div className="personal-info__field">

                            <label className="personal-info__label">
                                IMDB
                            </label>

                            <InputFieldV2
                                name={
                                    apiConstants.IMDB_link
                                }
                                placeholder="Enter your IMDB link"
                                type="text"
                            />

                        </div>

                        {/* Bio */}

                        <div style={{ marginTop: 15 }} className="about__bio-field">

                            <label className="about__label">
                                Bio
                            </label>

                            <InputFieldV2
                                placeholder="Enter biography"
                                rows={5}
                                type="text"
                                isTextArea
                                name={
                                    apiConstants.biography
                                }
                            />

                        </div>

                        {/* Working Days */}

                        <div className="about__days">

                            <p className="about__days-label">
                                Select the days
                                of the week you
                                can work.
                            </p>

                            <div className="about__days-grid">

                                {daysData.map(
                                    (day) => (
                                        <button
                                            key={
                                                day.value
                                            }
                                            type="button"
                                            className={`about__day-btn ${workingDays.includes(
                                                day.value
                                            )
                                                ? "about__day-btn--active"
                                                : ""
                                                }`}
                                            onClick={() =>
                                                toggleDay(
                                                    day.value
                                                )
                                            }
                                        >
                                            {
                                                day.label
                                            }
                                        </button>
                                    )
                                )}

                            </div>

                            <ErrorMessage
                                errors={
                                    errors
                                }
                                name={
                                    apiConstants.working_days
                                }
                                render={({
                                    message,
                                }) => (
                                    <span style={{ marginTop: '-10px' }} className="input-field-error-text">
                                        {
                                            message
                                        }
                                    </span>
                                )}
                            />

                        </div>

                        {/* Footer */}

                        <div className="register-step__footer">

                            <button
                                className="btn-next btn-next--yellow"
                                type="submit"
                            >
                                Next
                            </button>

                        </div>

                    </div>

                </form>

            </FormProvider>

        </RegisterLayout>
    );
};

export default StepAboutYourself;